import React from "react";
import type { Member, StytchRoleId } from "../../services/adminService";
import MemberRow from "./MemberRow";

export interface MemberTableProps {
  members: Member[];
  organizations: Record<string, string>;
  loading: boolean;
  error: string | null;
  searchQuery: string;
  onRetry: () => void;
  onChangeRole: (member: Member, nextRole: StytchRoleId) => void;
  onDeactivate: (member: Member) => void;
  onCancelInvite: (member: Member) => void;
}

/**
 * MemberTable — admin member-management table.
 *
 * Renders one of four states:
 *   - loading (initial only): 5 skeleton rows with animate-pulse content shapes
 *   - error: cloud_off icon + "Failed to load members" + Retry button
 *   - empty: contextual empty state — "No members yet" or "No members found"
 *   - populated: full table with one MemberRow per member
 */
const MemberTable: React.FC<MemberTableProps> = ({
  members,
  organizations,
  loading,
  error,
  searchQuery,
  onRetry,
  onChangeRole,
  onDeactivate,
  onCancelInvite,
}) => {
  if (loading) {
    return (
      <div className="bg-surface-container-low rounded-lg border border-outline-variant/20 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-container border-b border-outline-variant/20">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Email</th>
              <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Name</th>
              <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Organization</th>
              <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Role</th>
              <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Status</th>
              <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400 w-10" />
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2, 3, 4].map((i) => (
              <tr key={i} className="border-b border-outline-variant/10 animate-pulse">
                <td className="px-4 py-3"><div className="h-4 bg-surface-container-high rounded w-48" /></td>
                <td className="px-4 py-3"><div className="h-4 bg-surface-container-high rounded w-32" /></td>
                <td className="px-4 py-3"><div className="h-4 bg-surface-container-high rounded w-32" /></td>
                <td className="px-4 py-3"><div className="h-5 bg-surface-container-high rounded-full w-16" /></td>
                <td className="px-4 py-3"><div className="h-5 bg-surface-container-high rounded-full w-14" /></td>
                <td className="px-4 py-3"><div className="h-4 bg-surface-container-high rounded w-5" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3 text-error">
        <span className="material-symbols-outlined text-4xl">cloud_off</span>
        <p className="font-label text-sm uppercase tracking-widest">Failed to load members</p>
        <p className="text-xs font-body text-slate-500 text-center max-w-xs">
          Check your connection and try again.
        </p>
        <button
          onClick={onRetry}
          className="px-4 py-2 bg-surface-container hover:bg-surface-container-high border border-outline-variant/30 rounded text-on-surface text-xs font-label uppercase tracking-wider transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (members.length === 0) {
    if (searchQuery) {
      return (
        <div className="flex flex-col items-center justify-center py-24 gap-3 text-slate-500">
          <span className="material-symbols-outlined text-4xl opacity-20">search_off</span>
          <p className="font-label text-sm uppercase tracking-widest opacity-60">No members found</p>
          <p className="text-xs font-body text-slate-500 text-center max-w-xs">
            No members match "{searchQuery}". Try a different email address.
          </p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3 text-slate-500">
        <span className="material-symbols-outlined text-4xl opacity-20">group</span>
        <p className="font-label text-sm uppercase tracking-widest opacity-60">No members yet</p>
        <p className="text-xs font-body text-slate-500 text-center max-w-xs">
          Invite your first team member to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-surface-container-low rounded-lg border border-outline-variant/20 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-container border-b border-outline-variant/20">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Email</th>
            <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Name</th>
            <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Organization</th>
            <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Role</th>
            <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400">Status</th>
            <th className="px-4 py-3 text-left text-xs font-label font-bold uppercase tracking-wider text-slate-400 w-10" />
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <MemberRow
              key={member.member_id}
              member={member}
              organizations={organizations}
              onChangeRole={onChangeRole}
              onDeactivate={onDeactivate}
              onCancelInvite={onCancelInvite}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default MemberTable;
